export interface SimulationGuide {
  keyIdea: string;
  instructions: string;
  whatToTry: string[];
  guidedSteps: {
    instruction: string;
    observe: string;
  }[];
}

export const SIMULATION_GUIDE: Record<string, SimulationGuide> = {
  // ─────────────────────────────────────────────────────────
  // UNIT 1: KINEMATICS
  // ─────────────────────────────────────────────────────────

  "position-displacement": {
    keyIdea:
      "Displacement is direction-aware — 10m right then 10m left gives zero displacement, not 20m.",
    instructions:
      "Use the 1D car to explore the difference between position, distance traveled, and displacement. Pay close attention to the sign of displacement when the car reverses direction. The position readout tells you where the car is; displacement tells you the net change from start to finish.",
    whatToTry: [
      "Drive the car 50m to the right, then 50m back to the left. Compare the distance traveled with the displacement shown.",
      "Set the car at position x = -20m and drive it to x = +30m. What displacement does the sim report? Verify it equals x_final minus x_initial.",
      "Make a round trip: go right 80m, then back 30m. Predict the displacement before checking.",
      "Start at the origin and move only to the left. Notice that displacement is negative when you move in the negative direction.",
    ],
    guidedSteps: [
      {
        instruction:
          "Place the car at the origin (x = 0m) and drive it 40m to the right.",
        observe:
          "Both distance traveled (40m) and displacement (+40m) are the same when you move in one direction.",
      },
      {
        instruction: "Now reverse the car and drive it 40m back to the left.",
        observe:
          "Distance traveled is now 80m, but displacement is 0m. Displacement only cares about start and end positions.",
      },
      {
        instruction:
          "Reset. Drive 60m right, then 20m left. Calculate displacement in your head before reading it.",
        observe:
          "Displacement is +40m (60 - 20), while distance traveled is 80m (60 + 20). They only match for one-way trips.",
      },
      {
        instruction:
          "Try starting at x = -30m and ending at x = +10m.",
        observe:
          "Displacement is +40m regardless of where you started. It depends only on the change in position: (+10) - (-30) = +40m.",
      },
      {
        instruction:
          "Make several back-and-forth trips and watch the displacement value update in real time.",
        observe:
          "Displacement can increase or decrease and even become negative, while distance traveled only ever increases.",
      },
    ],
  },

  "velocity-speed": {
    keyIdea:
      "Speed tells you how fast; velocity tells you how fast AND which way — a car going 60 km/h east has a different velocity than one going 60 km/h west.",
    instructions:
      "Use the 1D car simulation to compare speed (always positive) with velocity (signed). Focus on what happens to the velocity readout when the car changes direction. Adjust the car's rate of travel and direction to build intuition about the difference between average and instantaneous values.",
    whatToTry: [
      "Drive the car at a steady rate to the right, then at the same rate to the left. Compare the speed and velocity readings in each direction.",
      "Make a round trip and compare average speed (total distance / total time) with average velocity (displacement / total time).",
      "Drive quickly to the right for 2 seconds, then slowly to the left for 4 seconds. Is average velocity positive or negative?",
      "Try to create a trip where average speed is large but average velocity is nearly zero.",
    ],
    guidedSteps: [
      {
        instruction:
          "Set the car moving to the right at a constant rate. Note both the speed and velocity displays.",
        observe:
          "Speed and velocity have the same magnitude. Velocity is positive because the car moves in the positive (right) direction.",
      },
      {
        instruction:
          "Reverse the car so it moves to the left at the same rate.",
        observe:
          "Speed stays the same, but velocity becomes negative. The sign of velocity encodes direction.",
      },
      {
        instruction:
          "Drive 100m to the right in 10s, then 100m back to the left in 10s.",
        observe:
          "Average speed is 200m/20s = 10 m/s. Average velocity is 0m/20s = 0 m/s. The round trip kills the average velocity.",
      },
      {
        instruction:
          "Now drive 100m right in 5s, then 60m left in 10s. Calculate both averages.",
        observe:
          "Average speed = 160m/15s ≈ 10.7 m/s. Average velocity = +40m/15s ≈ +2.7 m/s. They differ because the trip is not symmetric.",
      },
      {
        instruction:
          "Pause mid-trip and look at the instantaneous velocity.",
        observe:
          "Instantaneous velocity is the velocity at a single moment — it can differ greatly from the average over the whole trip.",
      },
    ],
  },

  acceleration: {
    keyIdea:
      "Acceleration is how quickly velocity changes — an object slowing down is accelerating too, just in the opposite direction of motion.",
    instructions:
      "Use the free-fall simulation to study acceleration due to gravity. Drop objects from different heights and observe how velocity increases at a constant rate. Focus on the velocity readout as the object falls — the steady increase IS constant acceleration. Remember: g ≈ 9.8 m/s² downward.",
    whatToTry: [
      "Drop a ball from rest and watch the velocity reading every second. Confirm it increases by roughly 9.8 m/s each second.",
      "Drop a ball from rest and then from an initial upward velocity. Compare how acceleration behaves in both cases.",
      "Try setting initial velocity upward. Notice that acceleration is STILL downward even while the ball rises.",
      "Compare drops from 20m and 80m. Does the acceleration change with height?",
    ],
    guidedSteps: [
      {
        instruction:
          "Drop a ball from rest (initial velocity = 0). Watch the velocity display as it falls.",
        observe:
          "Velocity increases by about 9.8 m/s every second. This constant rate of change is what we call acceleration.",
      },
      {
        instruction:
          "Record the velocity at t = 1s, 2s, and 3s. Calculate the change in velocity between each pair.",
        observe:
          "The change is the same each time (~9.8 m/s). Constant acceleration means equal velocity changes in equal time intervals.",
      },
      {
        instruction:
          "Now launch the ball upward with an initial velocity of +20 m/s.",
        observe:
          "The ball slows down on the way up, stops momentarily, then speeds up on the way down. Acceleration is -9.8 m/s² the ENTIRE time, even at the top.",
      },
      {
        instruction:
          "At the very top of the trajectory, note the velocity and acceleration.",
        observe:
          "Velocity is zero at the peak, but acceleration is still -9.8 m/s². Zero velocity does NOT mean zero acceleration.",
      },
      {
        instruction:
          "Change the drop height from 20m to 80m and compare the acceleration values.",
        observe:
          "Acceleration is the same regardless of drop height. Near Earth's surface, gravitational acceleration is constant.",
      },
    ],
  },

  "kinematic-equations": {
    keyIdea:
      "The four kinematic equations are just different ways to connect position, velocity, acceleration, and time — pick the one that has the three quantities you know and the one you want.",
    instructions:
      "Use the projectile motion simulation as a kinematic equation laboratory. By setting initial speed, angle, and reading off time, range, and height, you can verify each kinematic equation. Treat the horizontal and vertical motions independently — horizontal has zero acceleration, vertical has g downward.",
    whatToTry: [
      "Launch at 45° with v₀ = 20 m/s. Use v = v₀ + at to predict the vertical velocity at t = 1s, then check against the sim.",
      "Predict total flight time using the equation for vertical displacement (y = 0 at landing) and compare with the sim's timer.",
      "Calculate the maximum height using v² = v₀² + 2aΔy (set v = 0 at the top) and verify with the sim.",
      "Launch horizontally (0°) from a height. Predict landing time using y = ½gt² and check it.",
    ],
    guidedSteps: [
      {
        instruction:
          "Launch a projectile at 30° with v₀ = 25 m/s. Calculate the initial vertical velocity: v₀y = 25 sin(30°) = 12.5 m/s.",
        observe:
          "The sim should confirm the initial vertical component matches your calculation.",
      },
      {
        instruction:
          "Use v_y = v₀y - gt to predict vertical velocity at t = 1s: v_y = 12.5 - 9.8(1) = 2.7 m/s.",
        observe:
          "The velocity readout at t = 1s should be close to 2.7 m/s upward. The kinematic equation works.",
      },
      {
        instruction:
          "Find time to peak: set v_y = 0, solve t = v₀y / g = 12.5 / 9.8 ≈ 1.28s.",
        observe:
          "The projectile reaches its highest point at roughly 1.28s. Total flight time is about double this: ~2.55s.",
      },
      {
        instruction:
          "Calculate max height: Δy = v₀y² / (2g) = 12.5² / 19.6 ≈ 7.97m.",
        observe:
          "The sim's maximum height readout should be approximately 8m, confirming v² = v₀² + 2aΔy.",
      },
      {
        instruction:
          "Calculate the range: R = v₀x × t_total = 25 cos(30°) × 2.55 ≈ 55.2m.",
        observe:
          "The horizontal range in the sim should be close to 55m. Horizontal motion uses the simplest kinematic equation: x = v₀x × t (no horizontal acceleration).",
      },
      {
        instruction:
          "Change the angle to 60° (same speed). Predict the new range before launching.",
        observe:
          "The range is nearly the same as 30° — complementary angles give equal range. This is a direct consequence of the kinematic equations.",
      },
    ],
  },

  "free-fall": {
    keyIdea:
      "In free fall, every object accelerates at the same rate (g ≈ 9.8 m/s²) regardless of mass — a bowling ball and a tennis ball hit the ground at the same time when air resistance is negligible.",
    instructions:
      "Use the free-fall simulation to investigate motion under gravity alone. The key insight is that mass does NOT affect free-fall acceleration. Toggle air resistance on and off to see why Galileo's famous result only holds in a vacuum. Focus on the acceleration readout — it should stay constant throughout the fall.",
    whatToTry: [
      "Drop two objects of very different masses simultaneously (no air resistance). Do they land at the same time?",
      "Drop a ball from 80m with no initial velocity. Predict the landing time using t = √(2h/g) and check.",
      "Turn on air resistance and drop a light vs. heavy object. Now which lands first, and why?",
      "Drop from 5m, 20m, and 80m. Does doubling height double the fall time?",
    ],
    guidedSteps: [
      {
        instruction:
          "Drop a 1 kg ball and a 10 kg ball simultaneously from 45m with air resistance OFF.",
        observe:
          "Both land at the exact same time. In free fall (no air resistance), mass is irrelevant. Galileo was right.",
      },
      {
        instruction:
          "Record the fall time from 45m. Calculate it: t = √(2 × 45 / 9.8) ≈ 3.03s.",
        observe:
          "The sim's timer should match ~3.03s. This comes directly from h = ½gt².",
      },
      {
        instruction:
          "Now drop from 20m. Predict the time: t = √(2 × 20 / 9.8) ≈ 2.02s.",
        observe:
          "Fall time from 20m is about 2s, not half of 3s. Time goes as the square root of height, not linearly.",
      },
      {
        instruction:
          "Drop from 45m again and record the velocity just before impact: v = gt = 9.8 × 3.03 ≈ 29.7 m/s.",
        observe:
          "The impact velocity matches. You can also verify with v² = 2gh: v = √(2 × 9.8 × 45) ≈ 29.7 m/s.",
      },
      {
        instruction:
          "Turn ON air resistance. Drop the 1 kg ball and the 10 kg ball from the same height.",
        observe:
          "Now the heavier ball lands first. Air resistance depends on shape and speed, not mass, so it affects the lighter ball more relative to its weight.",
      },
    ],
  },

  "projectile-motion": {
    keyIdea:
      "A projectile's horizontal and vertical motions are completely independent — gravity only affects the vertical component, so horizontal velocity stays constant throughout the flight.",
    instructions:
      "Use the projectile motion simulation to explore how launch angle and speed affect range, height, and flight time. The crucial principle is independence of horizontal and vertical motion: horizontal velocity is constant (no horizontal force), while vertical velocity changes at rate g. Experiment with different angles to find the one that maximizes range.",
    whatToTry: [
      "Launch at 15°, 30°, 45°, 60°, and 75° with the same speed. Which angle gives the greatest range? The greatest height?",
      "Launch at 45° and watch the horizontal velocity throughout the flight. Does it change?",
      "Fire horizontally (0°) from a height and drop a ball from the same height simultaneously. Which hits the ground first?",
      "Set v₀ = 30 m/s at 45°. Predict the range using R = v₀² sin(2θ)/g before launching.",
    ],
    guidedSteps: [
      {
        instruction:
          "Launch a projectile at 45° with v₀ = 20 m/s. Record the range, max height, and flight time.",
        observe:
          "At 45°, the range is maximized for a given speed on flat ground. Range ≈ v₀²/g ≈ 40.8m.",
      },
      {
        instruction:
          "Now launch at 30° with the same speed. Record the same three quantities.",
        observe:
          "The range is shorter and the max height is lower compared to 45°. The flight time is also shorter.",
      },
      {
        instruction:
          "Launch at 60° (the complement of 30°) with the same speed.",
        observe:
          "The range is the same as 30°! Complementary angles yield the same range. However, the 60° shot goes much higher and takes longer.",
      },
      {
        instruction:
          "Watch the horizontal velocity component throughout a 45° launch.",
        observe:
          "Horizontal velocity remains constant the entire flight. There is no horizontal acceleration (ignoring air resistance), so v_x never changes.",
      },
      {
        instruction:
          "Watch the vertical velocity component throughout the same launch.",
        observe:
          "Vertical velocity starts positive (upward), decreases to zero at the peak, then becomes negative (downward). It changes by 9.8 m/s every second — that is gravitational acceleration at work.",
      },
      {
        instruction:
          "Fire horizontally from a raised platform and simultaneously drop a ball from the same height.",
        observe:
          "Both hit the ground at the same time. The horizontal velocity of the projectile has no effect on the vertical fall time. The motions are truly independent.",
      },
    ],
  },

  "motion-graphs": {
    keyIdea:
      "The slope of a position-time graph IS velocity, and the slope of a velocity-time graph IS acceleration — graphs are not just pictures, they encode the physics.",
    instructions:
      "Use the motion graphs simulation to see position-time, velocity-time, and acceleration-time graphs update in real time as an object moves. Focus on reading the graphs: a steeper slope on x-t means faster motion, a horizontal line on v-t means constant velocity, and the area under v-t gives displacement. Practice going from one graph to another.",
    whatToTry: [
      "Create constant velocity motion. What do the x-t, v-t, and a-t graphs each look like?",
      "Create constant acceleration from rest. The x-t graph should be a parabola — verify this.",
      "Make the object speed up, then slow down, then reverse. Identify each phase on all three graphs.",
      "Find a motion where the v-t graph crosses zero. What does that crossing mean on the x-t graph?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set the object to move at a constant velocity of +5 m/s. Examine all three graphs.",
        observe:
          "x-t is a straight line with positive slope (slope = 5 m/s). v-t is a horizontal line at 5. a-t is a horizontal line at 0. Constant velocity means zero acceleration.",
      },
      {
        instruction:
          "Now set constant acceleration of +2 m/s² starting from rest.",
        observe:
          "x-t is a parabola curving upward (position grows quadratically). v-t is a straight line with slope 2 m/s² (velocity grows linearly). a-t is a horizontal line at 2.",
      },
      {
        instruction:
          "Set the object moving at +10 m/s with acceleration -3 m/s². Watch all graphs.",
        observe:
          "The object slows down, stops momentarily (v-t crosses zero), then reverses. On the x-t graph, the peak corresponds to the instant velocity is zero.",
      },
      {
        instruction:
          "Look at the area under the v-t graph from t = 0 to the moment the object stops.",
        observe:
          "That area equals the displacement during that interval. Area under v-t = displacement is one of the most powerful graph-reading tools.",
      },
      {
        instruction:
          "Create a motion where acceleration changes (e.g., +2 m/s² for 3s, then 0 for 2s, then -2 m/s² for 3s).",
        observe:
          "The v-t graph has three distinct segments: a rising line, a flat line, and a falling line. The x-t graph transitions from a concave-up parabola to a straight line to a concave-down parabola.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // UNIT 2: DYNAMICS
  // ─────────────────────────────────────────────────────────

  "newtons-first-law": {
    keyIdea:
      "An object won't change its motion unless a NET force acts on it — a book on a table isn't 'at rest because nothing acts on it,' it's at rest because the forces cancel out.",
    instructions:
      "Use the friction simulation to explore Newton's first law (the law of inertia). The key idea is that zero net force means zero acceleration, NOT zero motion. Set up scenarios where forces balance and observe that the object either stays still or moves at constant velocity. Then unbalance the forces and watch what happens.",
    whatToTry: [
      "Apply a force equal to the friction force. The object should move at constant velocity — confirm net force is zero.",
      "Set all forces to zero on a moving object (friction OFF). Does it stop?",
      "Apply a small force, then increase friction until the object stops accelerating. What is special about that friction value?",
      "Remove all forces from a stationary object. Verify it stays put — no force is needed to maintain rest.",
    ],
    guidedSteps: [
      {
        instruction:
          "Place the block at rest with no applied force and friction turned on. Note the free-body diagram.",
        observe:
          "The block stays at rest. Normal force and gravity cancel vertically. No horizontal forces exist, so net force is zero. First law: object at rest stays at rest.",
      },
      {
        instruction:
          "Turn friction OFF and give the block an initial velocity by tapping it.",
        observe:
          "The block keeps moving at constant velocity forever. With no friction and no applied force, net force is zero, so there is no acceleration. Objects do NOT naturally slow down.",
      },
      {
        instruction:
          "Now turn friction ON with a moving block.",
        observe:
          "The block slows and stops. Friction is a net force opposing motion, causing deceleration. The block stops not because 'motion requires force' but because friction removes its velocity.",
      },
      {
        instruction:
          "Apply a horizontal force exactly equal to kinetic friction on a moving block.",
        observe:
          "The block moves at constant velocity. Applied force and friction cancel, net force is zero, so acceleration is zero. Constant velocity does NOT require zero force — it requires zero NET force.",
      },
      {
        instruction:
          "Increase the applied force slightly above friction.",
        observe:
          "Now there is a net force, so the block accelerates. Any unbalanced force causes acceleration — that is the essence of Newton's first law as a gateway to the second.",
      },
    ],
  },

  "newtons-second-law": {
    keyIdea:
      "F = ma is not just a formula — it says that acceleration is directly proportional to net force and inversely proportional to mass, so doubling force doubles acceleration but doubling mass halves it.",
    instructions:
      "Use the Newton's second law simulation to systematically vary force and mass and measure the resulting acceleration. The simulation lets you apply known forces to objects of known mass, making it a direct test of F = ma. Focus on the proportional relationships: keep one variable fixed while changing the other.",
    whatToTry: [
      "Keep mass at 2 kg. Apply 5 N, 10 N, 15 N, and 20 N. Plot acceleration vs. force — is it linear?",
      "Keep force at 10 N. Set mass to 1 kg, 2 kg, 4 kg, and 8 kg. Plot acceleration vs. mass — is it a straight line or a curve?",
      "Apply 20 N to a 4 kg block. Predict the acceleration before running the sim.",
      "Add friction and recalculate: if applied force is 20 N and friction is 8 N, what net force gives what acceleration for a 4 kg block?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set mass to 1 kg and apply a force of 10 N (no friction). Read the acceleration.",
        observe:
          "Acceleration = 10 m/s². With 1 kg, the acceleration numerically equals the net force. a = F/m = 10/1 = 10.",
      },
      {
        instruction:
          "Keep mass at 1 kg, increase force to 20 N.",
        observe:
          "Acceleration doubles to 20 m/s². Doubling force doubles acceleration when mass stays constant. This is the direct proportionality.",
      },
      {
        instruction:
          "Now set force back to 10 N but increase mass to 2 kg.",
        observe:
          "Acceleration is 5 m/s² — half of what it was. Doubling mass halves acceleration. This is the inverse proportionality.",
      },
      {
        instruction:
          "Set mass to 5 kg, apply 30 N, and add 10 N of friction.",
        observe:
          "Net force = 30 - 10 = 20 N. Acceleration = 20/5 = 4 m/s². F in F = ma is always the NET force, not just any single force.",
      },
      {
        instruction:
          "Try to find the force needed to accelerate a 3 kg block at exactly 7 m/s² (no friction).",
        observe:
          "You need F = ma = 3 × 7 = 21 N. The equation works both ways: given any two of F, m, a, you can find the third.",
      },
    ],
  },

  "newtons-third-law": {
    keyIdea:
      "When you push a wall, the wall pushes you back equally hard — every force has an equal and opposite partner, always on a DIFFERENT object.",
    instructions:
      "Use the collisions simulation to see Newton's third law in action. During a collision, the force that ball A exerts on ball B is exactly equal and opposite to the force that ball B exerts on ball A, at every instant. Watch the force vectors on both objects during impact. The forces are equal even when the masses are wildly different.",
    whatToTry: [
      "Collide two balls of equal mass. Watch the force vectors during collision — are they equal and opposite?",
      "Collide a 10 kg ball with a 1 kg ball. The forces during collision are STILL equal and opposite — verify this.",
      "If the forces are equal, why does the lighter ball accelerate more? Think about F = ma and check the accelerations.",
      "Try a perfectly inelastic collision (balls stick together). Are the third-law force pairs still equal during the collision?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set two balls of equal mass (5 kg each) moving toward each other at the same speed. Collide them and watch the force arrows.",
        observe:
          "During the collision, both force arrows are the same length but point in opposite directions. The force on A from B equals the force on B from A.",
      },
      {
        instruction:
          "Now set ball A to 10 kg and ball B to 1 kg, both at the same speed. Watch the forces during collision.",
        observe:
          "The force arrows are STILL equal in magnitude and opposite in direction. Third law does not care about mass — the forces are always an equal pair.",
      },
      {
        instruction:
          "With the 10 kg and 1 kg collision, compare the accelerations of both balls during impact.",
        observe:
          "The 1 kg ball accelerates 10 times more than the 10 kg ball. Same force, but a = F/m — less mass means more acceleration. This is why the lighter object bounces away faster.",
      },
      {
        instruction:
          "Compare the velocity changes (Δv) of each ball after the collision.",
        observe:
          "The lighter ball has a much larger change in velocity. Equal forces applied for equal time produce equal impulses, but equal impulse means larger Δv for the smaller mass (Δv = impulse/m).",
      },
      {
        instruction:
          "Check whether momentum is conserved: compute m₁v₁ + m₂v₂ before and after.",
        observe:
          "Total momentum is conserved. This is a CONSEQUENCE of Newton's third law — equal and opposite forces for equal time means equal and opposite impulses, so the total momentum change is zero.",
      },
    ],
  },

  "free-body-diagrams": {
    keyIdea:
      "A free-body diagram shows ALL forces on ONE object — if you include forces on different objects, you are doing it wrong.",
    instructions:
      "Use the friction simulation to practice drawing and interpreting free-body diagrams. The sim displays force vectors on the block — compare what you see with the FBD you would draw on paper. Every contact and field interaction generates a force. Make sure you can identify gravity, normal force, applied force, and friction, and explain why each is present.",
    whatToTry: [
      "Place a block on a surface with no applied force. Identify every force in the FBD. Why is the block in equilibrium?",
      "Apply a horizontal force. Redraw the FBD. How many forces act now?",
      "Increase the applied force until the block moves. Did any forces in the FBD change? Which ones?",
      "Tilt the surface slightly. How does the FBD change from the flat-surface case?",
    ],
    guidedSteps: [
      {
        instruction:
          "Place a block at rest on a flat surface with no applied force. Examine the force diagram.",
        observe:
          "Two forces: gravity (mg) pointing down and normal force (N) pointing up. They are equal and opposite, so net force is zero and the block is in equilibrium.",
      },
      {
        instruction:
          "Apply a small horizontal force (less than maximum static friction).",
        observe:
          "Now there are three forces visible, but actually four: gravity (down), normal (up), applied force (horizontal), and static friction (opposing the applied force). The block does not move because all four forces produce zero net force.",
      },
      {
        instruction:
          "Increase the applied force until the block just starts to slide.",
        observe:
          "Static friction reached its maximum value (μₛN). Beyond this, the applied force exceeds friction, creating a net horizontal force and thus acceleration.",
      },
      {
        instruction:
          "While the block is sliding, compare the free-body diagram to the stationary case.",
        observe:
          "Kinetic friction replaces static friction and is usually smaller. The FBD looks similar, but now the horizontal forces do NOT cancel — there is a net force and the block accelerates.",
      },
      {
        instruction:
          "For each force in the diagram, identify the other object in the interaction pair (Newton's third law partner).",
        observe:
          "Gravity: Earth pulls block down, block pulls Earth up. Normal: surface pushes block up, block pushes surface down. Friction: surface pushes block left, block pushes surface right. Each force has a partner on a DIFFERENT object.",
      },
    ],
  },

  friction: {
    keyIdea:
      "Static friction is a self-adjusting force that matches the push until it reaches its limit — only then does the object start to slide, and kinetic friction takes over at a lower value.",
    instructions:
      "Use the friction simulation to explore both static and kinetic friction. Gradually increase the applied force and watch how friction responds. The key discovery is that static friction adjusts to match the applied force up to a maximum value (μₛN), then drops to kinetic friction (μₖN) once sliding begins. Change the mass and surface type to see how μ and N affect friction.",
    whatToTry: [
      "Gradually increase the applied force from 0 N. At what force does the block start moving? That is the maximum static friction.",
      "Once the block is sliding, compare the friction force to what it was just before sliding began. Is kinetic friction less than maximum static friction?",
      "Double the block's mass. How does the maximum static friction change? Does μ change?",
      "Try different surface materials. Which has the highest coefficient of friction?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set a block on a surface and slowly increase the applied horizontal force from 0 N. Watch the friction force readout.",
        observe:
          "Friction increases to match the applied force exactly. The block does not move. Static friction is whatever it needs to be to prevent sliding — up to a limit.",
      },
      {
        instruction:
          "Keep increasing the applied force until the block starts sliding. Note the force at which it breaks free.",
        observe:
          "The block starts sliding when the applied force exceeds μₛ × N. This is the maximum static friction force. For a horizontal surface, N = mg.",
      },
      {
        instruction:
          "Once sliding, read the friction force.",
        observe:
          "Kinetic friction is constant and lower than maximum static friction. This is why it is harder to start an object moving than to keep it moving.",
      },
      {
        instruction:
          "Double the mass of the block and find the new breakaway force.",
        observe:
          "Maximum static friction doubles too, because f_s,max = μₛ × N and N = mg doubled. The coefficient μₛ did not change — it is a property of the surfaces, not the weight.",
      },
      {
        instruction:
          "Set the applied force exactly equal to kinetic friction while the block is sliding.",
        observe:
          "The block moves at constant velocity. Net force is zero, so acceleration is zero. This is a dynamic equilibrium — motion without acceleration.",
      },
    ],
  },

  "tension-normal": {
    keyIdea:
      "Tension pulls along the rope and normal force pushes perpendicular to the surface — both are contact forces that adjust their magnitude to satisfy Newton's laws.",
    instructions:
      "Use the Atwood machine simulation to explore tension in ropes and normal forces at pulleys. In an Atwood machine, two masses hang from a rope over a pulley. Tension is the same throughout a massless rope. By varying the two masses, you can see how tension adjusts to produce the observed acceleration. Focus on drawing free-body diagrams for each mass separately.",
    whatToTry: [
      "Set both masses equal. What is the tension in the rope? What is the acceleration?",
      "Make one mass much larger than the other (e.g., 10 kg vs. 1 kg). How does tension compare to the weight of each mass?",
      "Predict the acceleration using a = (m₁ - m₂)g / (m₁ + m₂) and verify with the sim.",
      "Calculate tension using T = 2m₁m₂g / (m₁ + m₂) and check against the sim's readout.",
    ],
    guidedSteps: [
      {
        instruction:
          "Set both masses to 5 kg. Release the system and observe the acceleration and tension.",
        observe:
          "Acceleration is zero — the system is balanced. Tension equals mg = 49 N. Each mass is in equilibrium: tension up equals weight down.",
      },
      {
        instruction:
          "Change one mass to 7 kg, keep the other at 5 kg. Release the system.",
        observe:
          "The heavier side accelerates downward. Acceleration a = (7-5)×9.8/(7+5) = 1.63 m/s². The system accelerates together as a unit.",
      },
      {
        instruction:
          "Read the tension and compare it to each weight (68.6 N and 49 N).",
        observe:
          "Tension is between the two weights (about 57.2 N). For the heavier mass, tension is less than its weight (net downward force accelerates it down). For the lighter mass, tension exceeds its weight (net upward force accelerates it up).",
      },
      {
        instruction:
          "Verify the tension formula: T = 2(7)(5)(9.8)/(7+5) = 57.2 N.",
        observe:
          "The formula matches the sim. This formula comes directly from applying Newton's second law to each mass separately, then solving the two equations simultaneously.",
      },
      {
        instruction:
          "Try m₁ = 10 kg, m₂ = 1 kg. Before running, predict which direction the system moves and estimate a and T.",
        observe:
          "a = (10-1)(9.8)/(10+1) = 8.0 m/s². T = 2(10)(1)(9.8)/(11) = 17.8 N. Tension is much less than the heavy weight but much more than the light weight. The large mass difference produces large acceleration.",
      },
    ],
  },

  "inclined-planes": {
    keyIdea:
      "On an incline, gravity splits into two components: mg sin(θ) pulls the object down the slope, and mg cos(θ) pushes it into the surface — as the angle increases, more of gravity goes into acceleration.",
    instructions:
      "Use the inclined plane simulation to decompose gravitational force into components parallel and perpendicular to the slope. Adjust the angle and friction to see how the block's motion changes. The key skill is resolving forces along tilted axes: the parallel component (mg sinθ) drives motion down the slope, and the perpendicular component (mg cosθ) determines the normal force.",
    whatToTry: [
      "Set angle to 0° (flat). What is the component of gravity along the surface? What about at 90° (vertical)?",
      "Gradually increase the angle with friction on. At what angle does the block start sliding? This gives you μₛ = tan(θ).",
      "Set the angle to 30° with no friction. Predict the acceleration (g sin 30° = 4.9 m/s²) and verify.",
      "Add friction (μₖ = 0.3) at 30° and recalculate: a = g(sin30° - μₖcos30°). Does it match?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set the incline angle to 30° with no friction and release the block from rest.",
        observe:
          "The block accelerates down the slope at a = g sin(30°) = 9.8 × 0.5 = 4.9 m/s². Only the parallel component of gravity acts along the slope.",
      },
      {
        instruction:
          "Check the normal force displayed by the sim.",
        observe:
          "Normal force N = mg cos(30°) = mg × 0.866. It is less than mg because the surface only needs to support the perpendicular component of the weight. On a steeper slope, N is even smaller.",
      },
      {
        instruction:
          "Increase the angle to 45°. Note the new acceleration and normal force.",
        observe:
          "Acceleration = g sin(45°) = 6.93 m/s², larger than before. Normal force = mg cos(45°) = 0.707 mg, smaller than before. More of gravity goes into acceleration, less into the surface.",
      },
      {
        instruction:
          "Go back to 30° and turn ON friction with μₖ = 0.3. Release the block.",
        observe:
          "Friction = μₖN = 0.3 × mg cos(30°). Net acceleration = g(sin30° - 0.3 cos30°) = 9.8(0.5 - 0.26) = 2.35 m/s². Friction reduces the acceleration but does not stop the block.",
      },
      {
        instruction:
          "With friction on, gradually decrease the angle until the block stops accelerating or does not slide.",
        observe:
          "The block stops sliding when mg sinθ ≤ μₛ mg cosθ, or tanθ ≤ μₛ. The critical angle where sliding just begins is θ = arctan(μₛ). This is how you measure μₛ experimentally.",
      },
      {
        instruction:
          "At 90°, what are the parallel and perpendicular components?",
        observe:
          "At 90°, the parallel component equals mg (full gravity) and the perpendicular component is zero (no contact with surface). It is pure free fall. The incline formulas smoothly transition from flat surface to free fall.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // UNIT 3: CIRCULAR MOTION
  // ─────────────────────────────────────────────────────────

  "uniform-circular-motion": {
    keyIdea:
      "An object moving in a circle at constant speed is ALWAYS accelerating — the acceleration points toward the center, constantly changing the direction of velocity without changing its magnitude.",
    instructions:
      "Use the circular motion simulation to see that uniform circular motion requires a centripetal (center-seeking) acceleration even though speed is constant. Watch the velocity and acceleration vectors: velocity is always tangent to the circle, and acceleration always points inward. Change the radius and speed to see how centripetal acceleration depends on both.",
    whatToTry: [
      "Set a constant speed and watch the velocity vector as the object goes around. Does it change? In what way?",
      "Double the speed while keeping radius constant. How does the centripetal acceleration change? (It should quadruple.)",
      "Double the radius while keeping speed constant. How does centripetal acceleration change? (It should halve.)",
      "Remove the centripetal force mid-motion. What path does the object follow?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set an object moving in a circle of radius 5m at speed 10 m/s. Observe the velocity and acceleration vectors.",
        observe:
          "Velocity is tangent to the circle (perpendicular to the radius). Acceleration points directly toward the center. Speed is constant, but velocity (a vector) is always changing direction.",
      },
      {
        instruction:
          "Calculate centripetal acceleration: a_c = v²/r = 100/5 = 20 m/s². Check the sim.",
        observe:
          "The sim confirms a_c = 20 m/s². Even though the object moves at constant speed, it accelerates because its direction constantly changes.",
      },
      {
        instruction:
          "Increase speed to 20 m/s (same radius). Predict new a_c = 400/5 = 80 m/s².",
        observe:
          "Centripetal acceleration quadrupled when speed doubled. a_c is proportional to v², so going faster demands much more centripetal acceleration — this is why sharp turns at high speed are dangerous.",
      },
      {
        instruction:
          "Reset speed to 10 m/s but increase radius to 10m. Predict a_c = 100/10 = 10 m/s².",
        observe:
          "Centripetal acceleration halved when radius doubled. Larger circles are gentler turns requiring less centripetal acceleration at the same speed.",
      },
      {
        instruction:
          "Release the object from circular motion (remove the centripetal force) and observe what happens.",
        observe:
          "The object flies off in a straight line tangent to the circle at the point of release. Without centripetal force, there is no centripetal acceleration, and the object follows Newton's first law — straight-line motion.",
      },
    ],
  },

  "centripetal-force": {
    keyIdea:
      "Centripetal force is not a new type of force — it is whatever force (gravity, tension, friction, normal) happens to point toward the center and keeps the object in circular motion.",
    instructions:
      "Use the circular motion simulation to identify which real force provides the centripetal force in different scenarios. Adjust mass, speed, and radius to verify F_c = mv²/r. The key insight is that 'centripetal force' is a ROLE, not a type — gravity plays this role for orbits, tension for a ball on a string, friction for a car on a curve.",
    whatToTry: [
      "Set m = 2 kg, v = 5 m/s, r = 4m. Calculate F_c = mv²/r = 2(25)/4 = 12.5 N and verify with the sim.",
      "Double the mass. Does F_c double? Why?",
      "Keep mass and radius the same but halve the speed. How does F_c change?",
      "Find the maximum speed for a given F_c and radius. What happens if speed exceeds this?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set m = 3 kg, r = 6m, v = 4 m/s. Calculate the centripetal force: F_c = mv²/r = 3(16)/6 = 8 N.",
        observe:
          "The sim shows 8 N directed toward the center. This is the net inward force required to maintain circular motion.",
      },
      {
        instruction:
          "Increase mass to 6 kg, keeping v and r the same.",
        observe:
          "Centripetal force doubles to 16 N. More mass needs more force for the same circular path, just like F = ma predicts.",
      },
      {
        instruction:
          "Reset mass to 3 kg and double the speed to 8 m/s.",
        observe:
          "F_c = 3(64)/6 = 32 N — it quadrupled! Centripetal force depends on v², making speed the dominant factor.",
      },
      {
        instruction:
          "Now double the radius to 12m (keep v = 4 m/s, m = 3 kg).",
        observe:
          "F_c = 3(16)/12 = 4 N — it halved. Larger radius means a gentler curve, requiring less centripetal force.",
      },
      {
        instruction:
          "Set a maximum force (like maximum friction or maximum tension) and gradually increase speed until the object can no longer maintain circular motion.",
        observe:
          "When mv²/r exceeds the maximum available centripetal force, the object breaks away from the circle. This is why cars skid on icy curves (friction is too small) and why strings break when you spin a ball too fast.",
      },
    ],
  },

  "gravitational-force": {
    keyIdea:
      "Every mass attracts every other mass — the force weakens with the square of the distance, so doubling the distance cuts gravitational force to one quarter.",
    instructions:
      "Use the gravity and orbits simulation to explore Newton's law of universal gravitation: F = Gm₁m₂/r². Adjust the masses and distances between objects to see how gravitational force changes. Focus on the inverse-square relationship: the force decreases rapidly with distance. Observe how gravitational force is mutual — both objects experience the same magnitude of force.",
    whatToTry: [
      "Place two equal masses at a fixed distance. Note the gravitational force. Now double one mass — does force double?",
      "Keep masses the same but double the distance. Does the force drop to 1/4?",
      "Make one mass 100 times the other (like a planet and a moon). Verify both experience the same force magnitude.",
      "Move the objects very close together. What happens to the force? What about very far apart?",
    ],
    guidedSteps: [
      {
        instruction:
          "Place two equal masses (e.g., 10⁶ kg each) at a set distance. Note the gravitational force between them.",
        observe:
          "The force arrows on both objects are equal in magnitude and point toward each other. Gravitational force is always attractive.",
      },
      {
        instruction:
          "Double one of the masses while keeping distance the same.",
        observe:
          "The gravitational force doubles. Force is directly proportional to each mass: F = Gm₁m₂/r².",
      },
      {
        instruction:
          "Reset the mass and instead double the distance between the objects.",
        observe:
          "The force drops to one-quarter of its original value. This is the inverse-square law: doubling distance means 1/2² = 1/4 the force.",
      },
      {
        instruction:
          "Triple the distance from the original setup.",
        observe:
          "Force drops to one-ninth (1/3² = 1/9). The inverse-square law means gravity weakens very quickly with distance.",
      },
      {
        instruction:
          "Set one mass much larger than the other (simulating a planet-moon system) and check the force on each.",
        observe:
          "Both objects experience the exact same force magnitude (Newton's third law). The planet pulls the moon with the same force the moon pulls the planet. The planet accelerates less only because it has more mass (a = F/m).",
      },
    ],
  },

  orbits: {
    keyIdea:
      "An orbit is just falling — the object falls toward the planet but moves sideways fast enough that the surface curves away beneath it, so it never lands.",
    instructions:
      "Use the gravity and orbits simulation to explore orbital mechanics. Launch objects at different speeds and angles to see circular orbits, elliptical orbits, escape trajectories, and crashes. The key idea is that orbital motion IS free fall — gravity provides the centripetal force. For a stable circular orbit, the speed must be exactly right: v = √(GM/r).",
    whatToTry: [
      "Launch a satellite horizontally at various speeds. Find the speed that gives a circular orbit.",
      "Launch faster than circular orbit speed. What shape is the orbit? What about even faster?",
      "Launch slower than circular orbit speed. What happens?",
      "Place the satellite at twice the orbital radius. Does the required orbital speed change?",
    ],
    guidedSteps: [
      {
        instruction:
          "Place a small satellite near a large planet. Launch it horizontally with a slow speed.",
        observe:
          "The satellite curves downward and crashes into the planet. It is falling toward the planet and does not have enough sideways speed to miss the surface.",
      },
      {
        instruction:
          "Gradually increase the launch speed until the satellite completes a full orbit.",
        observe:
          "At the right speed, the satellite continuously falls toward the planet but always misses — the surface curves away. This is a roughly circular orbit.",
      },
      {
        instruction:
          "Increase the speed above the circular orbit value.",
        observe:
          "The orbit becomes elliptical — the satellite moves farther from the planet on one side. At even higher speeds, it reaches escape velocity and never returns.",
      },
      {
        instruction:
          "For the circular orbit, verify that gravitational force provides centripetal force: GMm/r² = mv²/r.",
        observe:
          "This simplifies to v = √(GM/r). The orbital speed depends only on the planet's mass and the orbital radius — NOT on the satellite's mass.",
      },
      {
        instruction:
          "Double the orbital radius and find the new circular orbit speed.",
        observe:
          "The orbital speed is lower at larger radius (v ∝ 1/√r). Satellites farther from Earth orbit more slowly. The ISS (at ~400 km altitude) orbits in ~90 minutes; the Moon (at ~384,000 km) takes ~27 days.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // UNIT 4: ENERGY
  // ─────────────────────────────────────────────────────────

  work: {
    keyIdea:
      "Work equals force times displacement times the cosine of the angle between them — push perpendicular to the motion and you do zero work, no matter how hard you push.",
    instructions:
      "Use the work simulation to explore how force, displacement, and angle combine to produce work. The formula W = Fd cos(θ) means only the component of force parallel to displacement does work. Vary the angle from 0° to 90° to 180° and watch work go from maximum positive to zero to maximum negative.",
    whatToTry: [
      "Apply a force at 0° (along the direction of motion). All force does work — verify W = Fd.",
      "Apply the same force at 90° (perpendicular to motion). Verify that W = 0. The force is there, but it does no work.",
      "Set the angle to 60°. Predict W = Fd cos(60°) = 0.5Fd and check.",
      "Apply a force at 180° (opposing motion). Verify that work is negative — the force removes energy from the object.",
    ],
    guidedSteps: [
      {
        instruction:
          "Apply F = 10 N to an object over d = 5m at angle θ = 0°. Calculate W = 10 × 5 × cos(0°) = 50 J.",
        observe:
          "Work = 50 J. When force is parallel to displacement, all of the force contributes to work. This is the maximum work for this force and displacement.",
      },
      {
        instruction:
          "Keep F = 10 N and d = 5m but change θ to 60°. Predict W = 10 × 5 × cos(60°) = 25 J.",
        observe:
          "Work = 25 J. Only the component of force along the displacement (F cos θ = 5 N) does work. The perpendicular component (F sin θ) pushes sideways but does no work.",
      },
      {
        instruction:
          "Set θ = 90°. The force is entirely perpendicular to motion.",
        observe:
          "Work = 0 J. This is why the normal force on a flat surface does no work: it is perpendicular to horizontal motion. It is also why gravity does no work on an object moving horizontally.",
      },
      {
        instruction:
          "Set θ = 180°. The force opposes the direction of motion.",
        observe:
          "Work is negative: W = 10 × 5 × cos(180°) = -50 J. Negative work means the force removes kinetic energy from the object. Friction always does negative work because it opposes motion.",
      },
      {
        instruction:
          "Try several angles between 0° and 180° and plot W vs. θ in your mind (or on paper).",
        observe:
          "W follows a cosine curve: maximum at 0°, zero at 90°, minimum (most negative) at 180°. The sign of work tells you whether energy is being added to or removed from the object.",
      },
    ],
  },

  "kinetic-energy": {
    keyIdea:
      "Kinetic energy depends on the SQUARE of speed — doubling your speed quadruples your kinetic energy, which is why car crashes at highway speeds are so much more dangerous.",
    instructions:
      "Use the conservation of energy simulation to focus on kinetic energy: KE = ½mv². Set up scenarios where an object speeds up or slows down and watch the KE bar change. Pay special attention to the v² dependence — small speed changes at high speeds produce large KE changes. Use the energy bar chart to see KE in real time.",
    whatToTry: [
      "Set m = 2 kg, v = 3 m/s. Calculate KE = ½(2)(9) = 9 J. Now double the speed to 6 m/s. Does KE double?",
      "Keep speed at 5 m/s and try m = 1 kg, 2 kg, 4 kg. How does KE scale with mass?",
      "Let an object slide down a frictionless hill. As height decreases, watch KE increase. Where is KE greatest?",
      "Try to find two different mass-speed combinations that give the same KE.",
    ],
    guidedSteps: [
      {
        instruction:
          "Set an object with m = 2 kg moving at v = 5 m/s on a flat surface. Note the kinetic energy.",
        observe:
          "KE = ½(2)(25) = 25 J. This is the energy the object has due to its motion.",
      },
      {
        instruction:
          "Increase the speed to 10 m/s (same mass).",
        observe:
          "KE = ½(2)(100) = 100 J. Doubling speed quadrupled KE. The v² dependence makes speed changes much more impactful than mass changes.",
      },
      {
        instruction:
          "Reset speed to 5 m/s and double the mass to 4 kg.",
        observe:
          "KE = ½(4)(25) = 50 J. Doubling mass only doubles KE. Mass enters linearly, so its effect is less dramatic than speed.",
      },
      {
        instruction:
          "Place the object at the top of a frictionless ramp and release it. Watch the KE bar as it descends.",
        observe:
          "KE increases as the object descends. Gravitational potential energy converts to kinetic energy. KE is maximum at the lowest point where speed is greatest.",
      },
      {
        instruction:
          "Note the speed at the bottom. Verify that KE gained equals PE lost: ½mv² = mgh.",
        observe:
          "The numbers match. The speed at the bottom depends only on the height, not the mass: v = √(2gh). This is because both KE and PE are proportional to mass, so mass cancels.",
      },
    ],
  },

  "potential-energy": {
    keyIdea:
      "Potential energy is stored energy waiting to happen — a ball at height h has gravitational PE = mgh that converts to kinetic energy as it falls.",
    instructions:
      "Use the conservation of energy simulation to explore gravitational potential energy (PE = mgh) and elastic potential energy (PE = ½kx²). Move objects to different heights and watch the PE bar change. The reference level for gravitational PE is arbitrary — what matters is the CHANGE in PE. Set up ramps and springs to see PE convert to KE and back.",
    whatToTry: [
      "Place an object at height h = 10m. Calculate PE = mgh. Now move it to h = 5m. Verify PE drops by half.",
      "Release the object from height h on a frictionless ramp. Verify that speed at the bottom is v = √(2gh) regardless of the ramp's shape.",
      "Compress a spring and release it into an object. Watch elastic PE convert to KE.",
      "Find the height from which a 2 kg object must fall to reach 10 m/s at the bottom.",
    ],
    guidedSteps: [
      {
        instruction:
          "Place a 3 kg object at height h = 10m. Calculate PE = mgh = 3(9.8)(10) = 294 J.",
        observe:
          "The PE bar shows 294 J. This energy is stored in the object's position within Earth's gravitational field.",
      },
      {
        instruction:
          "Lower the object to h = 5m.",
        observe:
          "PE drops to 147 J — exactly half. PE is directly proportional to height. The 147 J of lost PE had to go somewhere (it would become KE if the object fell freely).",
      },
      {
        instruction:
          "Release the object from 10m on a frictionless curved ramp. Record its speed at the bottom.",
        observe:
          "v = √(2gh) = √(2 × 9.8 × 10) = 14.0 m/s. All PE converted to KE: mgh = ½mv², and mass cancels out.",
      },
      {
        instruction:
          "Try a steeper ramp and then a gentler ramp from the same height.",
        observe:
          "The speed at the bottom is the same regardless of the ramp's shape. Energy conservation depends only on the height difference, not the path taken. This is because gravity is a conservative force.",
      },
      {
        instruction:
          "Now set the reference level (h = 0) at a different location and recalculate PE.",
        observe:
          "The absolute value of PE changes, but the DIFFERENCE in PE between two positions stays the same. Only changes in PE matter physically — the reference level is a convention.",
      },
    ],
  },

  "conservation-of-energy": {
    keyIdea:
      "Energy cannot be created or destroyed — in a closed system, the total energy (KE + PE) stays constant; energy just transforms from one form to another.",
    instructions:
      "Use the conservation of energy simulation to verify that total mechanical energy remains constant when only conservative forces act. Track KE and PE as an object moves along ramps, hills, and loops. Add friction to see energy 'lost' to thermal energy. The energy bar chart is your best tool — the total bar should stay the same height on frictionless surfaces.",
    whatToTry: [
      "Release a ball from the top of a frictionless hill. Does the total energy bar stay constant throughout the motion?",
      "At the top, energy is all PE. At the bottom, it is all KE. Verify KE at bottom = PE at top.",
      "Add friction. Now what happens to the total mechanical energy? Where does the lost energy go?",
      "Send a ball over a hill-valley-hill path (frictionless). Can it reach a second hill taller than the first?",
    ],
    guidedSteps: [
      {
        instruction:
          "Release a ball from h = 15m on a frictionless track. Watch the KE and PE bars as it descends.",
        observe:
          "As PE decreases, KE increases by exactly the same amount. The total energy bar stays constant. Energy is being converted, not created or destroyed.",
      },
      {
        instruction:
          "At the bottom (h = 0), note that KE equals the original PE.",
        observe:
          "KE_bottom = PE_top = mgh. All gravitational PE has been converted to kinetic energy. The ball is moving at its maximum speed at the lowest point.",
      },
      {
        instruction:
          "If the track goes back uphill on the other side (frictionless), how high does the ball rise?",
        observe:
          "The ball rises to exactly the same height it started from (15m). At the top, all KE is converted back to PE. It cannot go higher — that would create energy from nothing.",
      },
      {
        instruction:
          "Now add friction to the track and repeat.",
        observe:
          "The ball does not return to its original height. Total mechanical energy decreases. The 'missing' energy has been converted to thermal energy (heat) by friction. Energy is still conserved overall, but mechanical energy is not.",
      },
      {
        instruction:
          "With friction, track how much mechanical energy is lost and compare it to the friction force times the distance traveled.",
        observe:
          "Energy lost to friction = friction force × distance. This is the work done by friction, which converts mechanical energy to thermal energy. The work-energy theorem and energy conservation are two sides of the same coin.",
      },
    ],
  },

  power: {
    keyIdea:
      "Power is how FAST you do work — lifting 100 kg up 3 meters does the same work whether it takes 1 second or 1 hour, but the power is vastly different.",
    instructions:
      "Use the power simulation to explore P = W/t and P = Fv. Apply forces to objects and measure both the work done and the time taken. Power is the rate of energy transfer. A more powerful engine does the same work in less time — or more work in the same time. Pay attention to the units: 1 watt = 1 joule per second.",
    whatToTry: [
      "Do 100 J of work in 10 seconds vs. in 2 seconds. Compare the power in each case.",
      "Apply a constant force to an object and watch power change as the object speeds up. Why does P increase?",
      "Find the power needed to lift a 5 kg object at a constant velocity of 3 m/s. Use P = Fv.",
      "Compare two scenarios: lifting 10 kg to 5m height in 5s vs. 20 kg to 5m in 10s. Which requires more power?",
    ],
    guidedSteps: [
      {
        instruction:
          "Apply a force to do 200 J of work over 10 seconds. Calculate power: P = W/t = 200/10 = 20 W.",
        observe:
          "Power is 20 watts. One watt means one joule of energy is transferred every second.",
      },
      {
        instruction:
          "Now do the same 200 J of work in 5 seconds.",
        observe:
          "Power is 40 W — double the previous value. Same work, half the time, double the power. Power measures urgency.",
      },
      {
        instruction:
          "Apply a constant force F = 50 N to an object that is accelerating. Watch the instantaneous power readout.",
        observe:
          "Power increases as the object speeds up because P = Fv. Even though force is constant, the velocity increases, so the rate of doing work increases with time.",
      },
      {
        instruction:
          "Apply a force to lift a 5 kg object at constant velocity v = 2 m/s. The force equals mg = 49 N.",
        observe:
          "P = Fv = 49 × 2 = 98 W. At constant velocity, force equals weight (no net force), and power is simply the weight times the lifting speed.",
      },
      {
        instruction:
          "Double the lifting speed to 4 m/s.",
        observe:
          "Power doubles to 196 W. To lift the same weight faster, you need proportionally more power. This is why powerful engines are needed for vehicles that must accelerate quickly or maintain high speeds against friction.",
      },
    ],
  },

  "work-energy-theorem": {
    keyIdea:
      "The net work done on an object equals its change in kinetic energy — if positive net work is done, the object speeds up; if negative, it slows down.",
    instructions:
      "Use the conservation of energy simulation to verify the work-energy theorem: W_net = ΔKE = ½mv_f² - ½mv_i². Apply forces over distances and measure the change in kinetic energy. This theorem connects force-based analysis (Newton's laws) with energy-based analysis. It works even when forces are not constant.",
    whatToTry: [
      "Apply a 10 N net force over 5m to a 2 kg object starting from rest. W_net = 50 J should equal the final KE.",
      "Start an object at 5 m/s, apply a braking force over some distance. Verify W_net = KE_final - KE_initial.",
      "Let an object slide down a frictionless ramp. Calculate the net work done by gravity (mgh) and verify it equals ΔKE.",
      "Add friction: now W_net = W_gravity + W_friction. Verify ΔKE equals this net work.",
    ],
    guidedSteps: [
      {
        instruction:
          "Set a 2 kg object at rest. Apply a net force of 20 N over a distance of 4m. Calculate W_net = 20 × 4 = 80 J.",
        observe:
          "The object's final KE should be 80 J (starting from rest, so KE_i = 0). The final speed is v = √(2 × 80 / 2) = √80 ≈ 8.94 m/s.",
      },
      {
        instruction:
          "Verify: ½mv² = ½(2)(80) = 80 J. Does it match W_net?",
        observe:
          "Yes, W_net = ΔKE = 80 J. The work-energy theorem holds: the net work done on the object equals the change in its kinetic energy.",
      },
      {
        instruction:
          "Now start the object at v_i = 6 m/s (KE_i = ½ × 2 × 36 = 36 J) and apply 20 N over 4m.",
        observe:
          "W_net = 80 J. Final KE = 36 + 80 = 116 J. Final speed = √(2 × 116 / 2) = √116 ≈ 10.77 m/s. The work-energy theorem accounts for the initial KE.",
      },
      {
        instruction:
          "Apply a friction force opposing motion to a moving object. Friction does negative work.",
        observe:
          "Negative work decreases KE. The object slows down by an amount determined by |W_friction| = f × d. If friction removes all the KE, the object stops.",
      },
      {
        instruction:
          "Slide an object down a ramp with friction. Calculate W_gravity = mgh (positive) and W_friction = -f×d (negative). Find W_net.",
        observe:
          "W_net = mgh - fd. This equals ΔKE. The work-energy theorem elegantly combines all forces into a single energy equation. This is often easier than using Newton's second law directly.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // UNIT 5: MOMENTUM
  // ─────────────────────────────────────────────────────────

  "linear-momentum": {
    keyIdea:
      "Momentum = mass × velocity — a slow truck and a fast bullet can have the same momentum, but the truck has more mass while the bullet has more speed.",
    instructions:
      "Use the collisions simulation to explore linear momentum (p = mv). Set up objects with different masses and velocities and observe their momentum values before and after collisions. Momentum is a vector: direction matters. A 5 kg object at +4 m/s has p = +20 kg·m/s, while a 5 kg object at -4 m/s has p = -20 kg·m/s.",
    whatToTry: [
      "Set a 2 kg ball at 10 m/s and a 10 kg ball at 2 m/s. Both have p = 20 kg·m/s — verify this.",
      "Calculate total momentum before and after a collision. Is it conserved?",
      "Set one ball stationary and collide the other into it. Track where the momentum goes.",
      "Try two balls moving in opposite directions. Remember momentum is a vector: total p = m₁v₁ + m₂v₂ (with signs).",
    ],
    guidedSteps: [
      {
        instruction:
          "Set ball A: m = 3 kg, v = +8 m/s. Calculate p_A = 3 × 8 = +24 kg·m/s.",
        observe:
          "The sim shows p_A = +24 kg·m/s. Momentum has both magnitude AND direction (it is a vector).",
      },
      {
        instruction:
          "Set ball B: m = 6 kg, v = -2 m/s. Calculate p_B = 6 × (-2) = -12 kg·m/s.",
        observe:
          "Momentum is negative because the ball moves in the negative direction. The total momentum is 24 + (-12) = +12 kg·m/s.",
      },
      {
        instruction:
          "Let the balls collide. After the collision, calculate p_A + p_B again.",
        observe:
          "Total momentum is still +12 kg·m/s. The individual momenta changed, but their sum did not. This is conservation of momentum.",
      },
      {
        instruction:
          "Try to find different masses and velocities that give a total momentum of zero before the collision.",
        observe:
          "When total momentum is zero, the center of mass is stationary. After the collision, total momentum is still zero — the balls move symmetrically.",
      },
      {
        instruction:
          "Make one ball very massive (20 kg) and the other very light (1 kg) with the same speed in opposite directions. Collide them.",
        observe:
          "The light ball bounces back dramatically while the heavy ball barely changes velocity. But the momentum TRANSFER is equal and opposite — the heavy ball's large mass means a small velocity change corresponds to a large momentum.",
      },
    ],
  },

  impulse: {
    keyIdea:
      "Impulse = force × time = change in momentum — you can reduce the force of impact by extending the time (this is why airbags and crumple zones work).",
    instructions:
      "Use the collisions simulation to study impulse (J = FΔt = Δp). Focus on how force and time during a collision relate to the change in momentum. The crucial insight is that the same impulse (same Δp) can be delivered by a large force over a short time OR a small force over a long time. This is the physics behind safety engineering.",
    whatToTry: [
      "Collide a 5 kg ball at 10 m/s into a wall. Δp = 100 kg·m/s (assuming it stops). If collision lasts 0.1s, F = 1000 N.",
      "Make the collision last 1s (like hitting a padded wall). Same Δp, but now F = 100 N — ten times less.",
      "Collide two balls and compare the impulse on each. Are they equal and opposite?",
      "Set mass₁ = 10 kg and mass₂ = 1 kg. Both experience the same impulse — but how do their velocity changes compare?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set a 4 kg ball moving at +8 m/s toward a stationary 4 kg ball. After the collision, observe the velocity changes.",
        observe:
          "If elastic: ball A stops, ball B moves at +8 m/s. Ball A's impulse: Δp = 4(0 - 8) = -32 kg·m/s. Ball B's impulse: Δp = 4(8 - 0) = +32 kg·m/s. Equal and opposite.",
      },
      {
        instruction:
          "Check the collision time and calculate the average force on each ball.",
        observe:
          "F = Δp/Δt. Both balls experience the same magnitude of impulse over the same collision time, so they experience equal magnitude forces (Newton's third law).",
      },
      {
        instruction:
          "Now make ball B much heavier (e.g., 12 kg). Collide ball A (4 kg, +8 m/s) into it. Compare the impulses.",
        observe:
          "The impulse on each ball is STILL equal and opposite. Ball A might bounce back (larger Δv), while ball B barely moves (smaller Δv). Same impulse, different Δv because of different masses: Δv = J/m.",
      },
      {
        instruction:
          "Consider the impulse-momentum theorem: J = Δp. Calculate the impulse on ball A from its velocity change and mass.",
        observe:
          "J = mΔv gives the same result whether you compute it from force × time or mass × velocity change. These are two equivalent ways to calculate impulse.",
      },
      {
        instruction:
          "Think about a car crash: Δp is fixed (the car stops). An airbag increases Δt from ~0.05s to ~0.3s. How does this affect force?",
        observe:
          "Force = Δp/Δt. Increasing time by 6× reduces force by 6×. Same impulse, much less force — that is the principle behind every safety device that cushions an impact.",
      },
    ],
  },

  "conservation-of-momentum": {
    keyIdea:
      "In any collision or explosion with no external forces, total momentum before equals total momentum after — this works even when energy is NOT conserved.",
    instructions:
      "Use the collisions simulation to systematically verify conservation of momentum. Calculate total momentum (sum of all m×v) before and after each collision. Try elastic collisions, inelastic collisions, and perfectly inelastic (sticky) collisions. Momentum is conserved in ALL of them. Energy may or may not be conserved — that distinguishes the types.",
    whatToTry: [
      "Elastic collision: 5 kg at +6 m/s hits stationary 5 kg. Verify total p is conserved AND total KE is conserved.",
      "Perfectly inelastic: same setup but balls stick together. Verify total p is conserved but total KE is NOT conserved.",
      "Two balls moving toward each other: m₁v₁ + m₂v₂ before = m₁v₁' + m₂v₂' after. Verify with numbers.",
      "Explosion (reverse collision): one object splits into two. Total momentum before (zero) equals total after.",
    ],
    guidedSteps: [
      {
        instruction:
          "Set ball A: 4 kg at +10 m/s. Ball B: 4 kg at rest. Elastic collision. Calculate total p before: 4(10) + 4(0) = 40 kg·m/s.",
        observe:
          "After collision: ball A stops, ball B moves at +10 m/s. Total p after: 4(0) + 4(10) = 40 kg·m/s. Momentum conserved.",
      },
      {
        instruction:
          "Check KE before and after. Before: ½(4)(100) = 200 J. After: ½(4)(100) = 200 J.",
        observe:
          "KE is also conserved — that is what makes it elastic. Both momentum and kinetic energy are conserved in elastic collisions.",
      },
      {
        instruction:
          "Now make it perfectly inelastic (balls stick). Same initial setup.",
        observe:
          "After: combined 8 kg mass moves at +5 m/s. Total p = 8 × 5 = 40 kg·m/s. Momentum is still conserved! But KE after = ½(8)(25) = 100 J — only half the original 200 J remains. Energy was lost to deformation and heat.",
      },
      {
        instruction:
          "Set ball A: 6 kg at +5 m/s and ball B: 3 kg at -10 m/s (moving toward each other). Total p = 30 - 30 = 0.",
        observe:
          "Total momentum is zero. After the collision, no matter what type, total momentum must still be zero. The balls must either both stop or move in opposite directions with equal-magnitude momenta.",
      },
      {
        instruction:
          "Try a scenario where total initial momentum is NOT zero. Predict the final velocities using conservation of momentum.",
        observe:
          "With one equation (momentum conservation) and two unknowns (two final velocities), you need additional information: either the collision type (elastic → KE conserved, perfectly inelastic → objects stick) to solve completely.",
      },
    ],
  },

  collisions: {
    keyIdea:
      "Elastic collisions conserve BOTH momentum and kinetic energy; inelastic collisions conserve momentum but lose kinetic energy to heat, sound, and deformation.",
    instructions:
      "Use the collisions simulation to compare elastic, inelastic, and perfectly inelastic collisions. For each type, track both total momentum and total kinetic energy before and after. The defining characteristic is what happens to KE: elastic keeps it all, perfectly inelastic loses the maximum possible (while still conserving momentum), and everything in between is partially inelastic.",
    whatToTry: [
      "Run the same collision as elastic and perfectly inelastic. Compare final velocities and KE lost.",
      "In a perfectly inelastic collision between equal masses (one at rest), verify that exactly half the KE is lost.",
      "Collide a very heavy ball into a very light one (elastic). The heavy ball barely slows; the light ball flies away at nearly 2× the initial speed.",
      "Collide a very light ball into a very heavy one (elastic). The light ball bounces back at nearly its original speed; the heavy ball barely moves.",
    ],
    guidedSteps: [
      {
        instruction:
          "Elastic collision: 4 kg at +6 m/s hits 2 kg at rest. Record final velocities of both.",
        observe:
          "Ball A slows down, ball B speeds up. Using elastic collision equations: v₁' = (m₁-m₂)v₁/(m₁+m₂) = 2(6)/6 = 2 m/s. v₂' = 2m₁v₁/(m₁+m₂) = 48/6 = 8 m/s.",
      },
      {
        instruction:
          "Verify momentum conservation: before = 4(6) = 24. After = 4(2) + 2(8) = 24.",
        observe:
          "Momentum is conserved: 24 = 24. Now check KE: before = ½(4)(36) = 72 J. After = ½(4)(4) + ½(2)(64) = 8 + 64 = 72 J. KE is also conserved — elastic.",
      },
      {
        instruction:
          "Same initial setup, but perfectly inelastic (balls stick). Combined mass = 6 kg.",
        observe:
          "Final velocity = 24/6 = 4 m/s (from momentum conservation). Final KE = ½(6)(16) = 48 J. Lost KE = 72 - 48 = 24 J. One-third of the KE was lost to deformation.",
      },
      {
        instruction:
          "Elastic: send a 10 kg ball at 4 m/s into a 1 kg ball at rest.",
        observe:
          "The heavy ball barely slows (v₁' ≈ 3.27 m/s), the light ball shoots off fast (v₂' ≈ 7.27 m/s). Think of a bowling ball hitting a marble.",
      },
      {
        instruction:
          "Elastic: send a 1 kg ball at 4 m/s into a 10 kg ball at rest.",
        observe:
          "The light ball bounces back (v₁' ≈ -3.27 m/s), the heavy ball barely moves (v₂' ≈ 0.73 m/s). Think of a marble bouncing off a bowling ball. The light ball reverses direction.",
      },
      {
        instruction:
          "Compare the fraction of KE lost in perfectly inelastic collisions with equal masses vs. very unequal masses.",
        observe:
          "Equal masses: 50% of KE is lost. Unequal masses: less KE is lost because the lighter object carries away significant KE. The maximum fraction of KE is lost in a perfectly inelastic collision between equal masses.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // UNIT 6: SIMPLE HARMONIC MOTION
  // ─────────────────────────────────────────────────────────

  oscillations: {
    keyIdea:
      "Simple harmonic motion occurs whenever the restoring force is proportional to displacement — pull it farther from equilibrium and it pulls back harder, creating a smooth back-and-forth.",
    instructions:
      "Use the springs simulation to study the fundamentals of oscillation. Stretch or compress the spring and release — the mass oscillates about equilibrium. Focus on the relationships: the restoring force always points toward equilibrium, displacement varies sinusoidally with time, and the period does NOT depend on amplitude (for ideal SHM).",
    whatToTry: [
      "Pull the mass to different amplitudes. Does the period change? (It should NOT for ideal SHM.)",
      "Double the spring constant k. How does the period change?",
      "Double the mass m. How does the period change?",
      "Watch position, velocity, and acceleration at the equilibrium point and at maximum displacement. Where is each quantity maximum?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set a mass of 1 kg on a spring with k = 10 N/m. Pull it 0.2m from equilibrium and release.",
        observe:
          "The mass oscillates smoothly back and forth. This is simple harmonic motion. The period T = 2π√(m/k) = 2π√(1/10) ≈ 1.99s.",
      },
      {
        instruction:
          "Increase the amplitude to 0.4m. Measure the period again.",
        observe:
          "The period is the same (~1.99s). In SHM, the period is independent of amplitude. Bigger swings mean faster speeds to cover more distance in the same time.",
      },
      {
        instruction:
          "Double the spring constant to k = 20 N/m. Predict the new period: T = 2π√(1/20) ≈ 1.40s.",
        observe:
          "A stiffer spring produces a shorter period (faster oscillation). The restoring force is stronger, so it pulls the mass back more aggressively.",
      },
      {
        instruction:
          "Reset k = 10 N/m and change mass to 4 kg. Predict T = 2π√(4/10) ≈ 3.97s.",
        observe:
          "More mass means a longer period (slower oscillation). The mass has more inertia, so it takes longer to reverse direction.",
      },
      {
        instruction:
          "Watch the position, velocity, and acceleration graphs. Note their phase relationships.",
        observe:
          "Position is a cosine wave, velocity is its derivative (sine wave, 90° ahead), and acceleration is the second derivative (negative cosine, 180° out of phase with position). When displacement is maximum, velocity is zero; when displacement is zero, velocity is maximum.",
      },
    ],
  },

  springs: {
    keyIdea:
      "Hooke's law: the spring force is F = -kx — the force is proportional to how far you stretch or compress, and the minus sign means it always pulls back toward equilibrium.",
    instructions:
      "Use the springs simulation to verify Hooke's law quantitatively. Hang masses from the spring and measure extension to find k, then observe oscillation. The key relationship is F = -kx: double the displacement, double the force. The negative sign means the force opposes the displacement. Explore what happens at extreme compressions or extensions where real springs deviate from Hooke's law.",
    whatToTry: [
      "Hang 1 kg (weight = 9.8 N) and measure extension. Then hang 2 kg. Does extension double?",
      "Calculate k from F = kx. Verify it is the same for different masses.",
      "Stretch the spring to 0.1m and note the restoring force. Stretch to 0.3m. Is the force 3× larger?",
      "Oscillate the spring-mass system. Verify the period matches T = 2π√(m/k) using your measured k.",
    ],
    guidedSteps: [
      {
        instruction:
          "Attach a 1 kg mass to the spring. Measure the equilibrium extension x₁.",
        observe:
          "The spring stretches until the spring force (kx) balances gravity (mg). At equilibrium: kx₁ = (1)(9.8), so x₁ = 9.8/k.",
      },
      {
        instruction:
          "Attach a 2 kg mass. Measure the new extension x₂.",
        observe:
          "The extension doubles: x₂ = 2x₁. This confirms Hooke's law — double the force (weight) produces double the displacement. The spring constant k = mg/x.",
      },
      {
        instruction:
          "Calculate k from your measurements. For example, if 1 kg stretches it 0.1m, then k = 9.8/0.1 = 98 N/m.",
        observe:
          "The spring constant k is a property of the spring itself, not the mass. It tells you how stiff the spring is: bigger k means stiffer spring and less extension.",
      },
      {
        instruction:
          "Displace the mass from its new equilibrium and release. Measure the period of oscillation.",
        observe:
          "The period matches T = 2π√(m/k). Using k from the static measurement correctly predicts the dynamic (oscillation) behavior — Hooke's law governs both.",
      },
      {
        instruction:
          "Try a very large displacement. Does the spring still follow Hooke's law?",
        observe:
          "At very large extensions, real springs deviate from Hooke's law (the force is no longer proportional to displacement). Hooke's law is valid only for small to moderate deformations.",
      },
    ],
  },

  pendulums: {
    keyIdea:
      "A pendulum's period depends on length and gravity, NOT on mass or amplitude (for small swings) — T = 2π√(L/g), so a longer pendulum swings slower.",
    instructions:
      "Use the pendulum simulation to explore what affects the period of a simple pendulum. Change the length, mass, amplitude, and gravitational strength. The surprising result is that mass does NOT affect the period, and neither does amplitude (for small angles). Only the length of the pendulum and the gravitational field strength matter: T = 2π√(L/g).",
    whatToTry: [
      "Keep length at 1m and try masses of 1 kg, 5 kg, and 10 kg. Does the period change?",
      "Double the pendulum length. Does the period double? (No — it increases by √2.)",
      "Try small-angle swings (5°) and larger swings (30°, 60°). At what angle does the period start to change noticeably?",
      "Change the gravitational field (try g on the Moon ≈ 1.6 m/s²). How does the period change?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set L = 1m, m = 2 kg, small angle (< 15°). Measure the period.",
        observe:
          "T = 2π√(1/9.8) ≈ 2.01s. This is the period of a 1-meter pendulum on Earth. Grandfather clocks use approximately this length for a 2-second period.",
      },
      {
        instruction:
          "Change mass to 5 kg, keep everything else the same.",
        observe:
          "The period is still ~2.01s. Mass does NOT affect the period. This is because the restoring force (mg sinθ) and inertia (m) both scale with mass, so mass cancels in the equation of motion.",
      },
      {
        instruction:
          "Increase the length to 4m. Predict T = 2π√(4/9.8) ≈ 4.01s.",
        observe:
          "The period doubled. Quadrupling the length doubles the period (because T ∝ √L). Longer pendulums swing more slowly.",
      },
      {
        instruction:
          "Try amplitudes of 5°, 15°, 30°, and 60° with L = 1m.",
        observe:
          "At small angles (< ~15°), the period is essentially constant. At larger angles (30°+), the period starts to increase slightly. The formula T = 2π√(L/g) is a small-angle approximation.",
      },
      {
        instruction:
          "Change gravity to 1.6 m/s² (Moon). Predict T = 2π√(1/1.6) ≈ 4.97s.",
        observe:
          "On the Moon, the period is much longer — the pendulum swings sluggishly in weak gravity. T ∝ 1/√g, so weaker gravity means slower oscillation. Pendulums were historically used to measure g.",
      },
    ],
  },

  "energy-in-shm": {
    keyIdea:
      "In SHM, energy flows endlessly between kinetic and potential — at maximum displacement, all energy is potential; at equilibrium, all energy is kinetic; and the total never changes.",
    instructions:
      "Use the springs simulation with energy bar charts to track how energy transforms during simple harmonic motion. At the endpoints (maximum displacement), the mass is momentarily at rest — all energy is elastic PE. At the equilibrium point, the spring is relaxed — all energy is KE. Watch the bars swap as the mass oscillates. Total mechanical energy stays constant.",
    whatToTry: [
      "Pull the mass to maximum displacement. Note KE = 0 and PE = max. Release and watch the bars as it passes through equilibrium.",
      "At equilibrium, PE = 0 and KE = max. Verify that KE_max = PE_max = ½kA² (where A is the amplitude).",
      "Increase the amplitude. How does the total energy change? (It should go as A².)",
      "Add damping (friction). Watch total energy decrease over time. Which bar shrinks — KE, PE, or both?",
    ],
    guidedSteps: [
      {
        instruction:
          "Set k = 20 N/m, m = 2 kg, amplitude A = 0.5m. At maximum displacement, calculate PE = ½kA² = ½(20)(0.25) = 2.5 J.",
        observe:
          "At the endpoints, KE = 0 (mass is momentarily at rest) and PE = 2.5 J. All energy is stored in the spring.",
      },
      {
        instruction:
          "Release and watch the mass pass through equilibrium (x = 0).",
        observe:
          "At equilibrium, PE = 0 (spring is at natural length) and KE = 2.5 J. All energy is now kinetic. Total energy is still 2.5 J.",
      },
      {
        instruction:
          "At equilibrium, calculate the speed: KE = ½mv² → v = √(2 × 2.5 / 2) = √2.5 ≈ 1.58 m/s.",
        observe:
          "This is the maximum speed, which occurs at the equilibrium position. You can also derive it: v_max = A√(k/m) = 0.5√(20/2) = 0.5 × √10 ≈ 1.58 m/s.",
      },
      {
        instruction:
          "Watch the energy bars at an intermediate position (say, x = A/2 = 0.25m).",
        observe:
          "PE = ½k(0.25)² = 0.625 J. KE = 2.5 - 0.625 = 1.875 J. Both bars are non-zero. The energy is shared between kinetic and potential.",
      },
      {
        instruction:
          "Double the amplitude to A = 1.0m. How does total energy change?",
        observe:
          "Total energy = ½k(1.0)² = 10 J — four times the original 2.5 J. Doubling amplitude quadruples total energy because E ∝ A². This is why large oscillations carry much more energy.",
      },
      {
        instruction:
          "Add light damping and watch several oscillations.",
        observe:
          "The amplitude decreases each cycle and total energy gradually decreases. Both KE_max and PE_max decrease because the amplitude shrinks. Energy is being lost to friction (thermal energy). Eventually the oscillation dies out completely.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // UNIT 7: TORQUE AND ROTATIONAL MOTION
  // ─────────────────────────────────────────────────────────

  "angular-quantities": {
    keyIdea:
      "Angular quantities are the rotational twins of linear ones — angular displacement is like distance, angular velocity is like speed, and angular acceleration is like acceleration, all measured in radians.",
    instructions:
      "Use the angular motion simulation to explore angular position (θ), angular velocity (ω), and angular acceleration (α). These are the rotational analogs of x, v, and a. Watch a rotating object and observe how ω is the rate of change of θ, and α is the rate of change of ω. The connection to linear motion is through the radius: v = rω, a_tangential = rα.",
    whatToTry: [
      "Set a disc spinning at constant ω. How does θ change with time? (It should increase linearly.)",
      "Apply a constant angular acceleration. Watch ω increase linearly and θ increase quadratically — just like linear kinematics.",
      "Measure the linear speed of a point on the rim. Verify v = rω by trying different radii.",
      "Stop the angular acceleration. Does the disc stop spinning? (No — it spins at constant ω, just like Newton's first law for rotation.)",
    ],
    guidedSteps: [
      {
        instruction:
          "Set a disc spinning at a constant angular velocity of ω = 3 rad/s (no angular acceleration).",
        observe:
          "Angular position θ increases linearly with time: θ = ωt. After 2s, θ = 6 rad (about 344°, nearly one full revolution). The disc spins steadily.",
      },
      {
        instruction:
          "Note the linear speed of a point at the rim (r = 0.5m).",
        observe:
          "v = rω = 0.5 × 3 = 1.5 m/s. Points farther from the center move faster even though every point has the same angular velocity. This is why the outer edge of a merry-go-round feels faster.",
      },
      {
        instruction:
          "Apply a constant angular acceleration of α = 2 rad/s² starting from ω = 0.",
        observe:
          "ω increases linearly: ω = αt. After 3s, ω = 6 rad/s. Angular position grows quadratically: θ = ½αt² = ½(2)(9) = 9 rad. These are exactly the rotational kinematic equations.",
      },
      {
        instruction:
          "Remove the angular acceleration while the disc is spinning at ω = 6 rad/s.",
        observe:
          "The disc continues spinning at 6 rad/s indefinitely (no friction). This is rotational inertia — the rotational analog of Newton's first law. An object in rotation stays in rotation unless acted on by a net torque.",
      },
      {
        instruction:
          "Compare a point at r = 0.2m and r = 0.5m on the spinning disc.",
        observe:
          "Both have the same ω and α, but the outer point has larger v (= rω) and larger tangential acceleration (= rα). Angular quantities are the same for all points on a rigid body; linear quantities depend on distance from the axis.",
      },
    ],
  },

  torque: {
    keyIdea:
      "Torque is a force's ability to cause rotation — it depends not just on how hard you push, but WHERE and at WHAT ANGLE you push: τ = rF sin(θ).",
    instructions:
      "Use the torque simulation to apply forces at different points and angles on a rigid body. Torque (τ = rF sinθ) depends on three things: the magnitude of the force, the distance from the pivot (lever arm), and the angle between the force and the position vector. Maximize torque by pushing perpendicular to the lever arm. Explore how torques can balance (static equilibrium).",
    whatToTry: [
      "Apply F = 10 N at r = 0.5m perpendicular to the arm. Torque = 5 N·m. Now move to r = 1m. Torque doubles.",
      "Keep r = 1m, F = 10 N, but change the angle from 90° to 45° to 0°. Watch torque go from maximum to zero.",
      "Balance two different forces on opposite sides of the pivot. When is the system in rotational equilibrium?",
      "Push at the pivot point (r = 0). What happens? Why?",
    ],
    guidedSteps: [
      {
        instruction:
          "Apply F = 10 N perpendicular to a bar at r = 0.5m from the pivot.",
        observe:
          "Torque = rF sin(90°) = 0.5 × 10 × 1 = 5 N·m. The bar begins to rotate. Pushing perpendicular maximizes the torque.",
      },
      {
        instruction:
          "Move the same force to r = 1.0m from the pivot.",
        observe:
          "Torque = 1.0 × 10 = 10 N·m — it doubled. This is why door handles are far from the hinge: greater distance from the pivot means more torque for the same force.",
      },
      {
        instruction:
          "At r = 1.0m, change the angle from 90° to 45°.",
        observe:
          "Torque = 1.0 × 10 × sin(45°) = 7.07 N·m. Pushing at an angle reduces the torque because only the perpendicular component of the force contributes.",
      },
      {
        instruction:
          "Push directly toward or away from the pivot (θ = 0° or 180°).",
        observe:
          "Torque = rF sin(0°) = 0. A force directed along the lever arm produces zero torque. This is why pushing a door at the hinge (along the door) does not rotate it.",
      },
      {
        instruction:
          "Place a 20 N weight at 0.3m on one side and find the weight needed at 0.6m on the other side to balance.",
        observe:
          "For equilibrium: τ_CW = τ_CCW → 20 × 0.3 = F × 0.6 → F = 10 N. A smaller force at a greater distance can balance a larger force at a shorter distance. This is the principle of the lever.",
      },
    ],
  },

  "rotational-inertia": {
    keyIdea:
      "Rotational inertia (moment of inertia) is how hard it is to spin an object — it depends not just on mass, but on how far that mass is from the axis of rotation.",
    instructions:
      "Use the rotational inertia simulation to explore how mass distribution affects resistance to angular acceleration. The moment of inertia I depends on both mass and how it is distributed relative to the rotation axis. Apply the same torque to objects with different mass distributions and compare angular accelerations. This is the rotational analog of Newton's second law: τ = Iα.",
    whatToTry: [
      "Apply the same torque to a solid disc and a ring of equal mass and radius. Which accelerates faster?",
      "Move masses closer to or farther from the axis of rotation. How does the angular acceleration change?",
      "Verify τ = Iα: apply a known torque, measure α, and calculate I. Compare with the formula.",
      "Try spinning a barbell with weights at different positions along the bar.",
    ],
    guidedSteps: [
      {
        instruction:
          "Apply a torque of 10 N·m to a solid disc (I = ½MR²) with M = 4 kg, R = 0.5m. Calculate I = ½(4)(0.25) = 0.5 kg·m².",
        observe:
          "Angular acceleration α = τ/I = 10/0.5 = 20 rad/s². The disc accelerates angularly just as F = ma predicts linear acceleration.",
      },
      {
        instruction:
          "Apply the same torque to a ring (I = MR²) with the same mass and radius. I = 4(0.25) = 1.0 kg·m².",
        observe:
          "α = 10/1.0 = 10 rad/s² — half the disc's acceleration. The ring has twice the rotational inertia because all its mass is at the maximum distance from the axis.",
      },
      {
        instruction:
          "Take a bar with movable masses. Place two 1 kg masses at r = 0.1m from the center. Measure α for a given torque.",
        observe:
          "I = 2 × 1 × (0.1)² = 0.02 kg·m². The bar accelerates rapidly because the masses are close to the axis.",
      },
      {
        instruction:
          "Move the same masses to r = 0.5m. Apply the same torque.",
        observe:
          "I = 2 × 1 × (0.5)² = 0.5 kg·m² — 25 times larger! α drops by a factor of 25. Moving mass away from the axis dramatically increases rotational inertia.",
      },
      {
        instruction:
          "Predict: which rolls down a ramp faster — a solid sphere, a solid cylinder, or a hollow cylinder (all same mass and radius)?",
        observe:
          "The solid sphere (I = 2/5 MR²) wins, followed by the solid cylinder (I = 1/2 MR²), then the hollow cylinder (I = MR²). Less rotational inertia means more of the gravitational PE converts to translational KE rather than rotational KE.",
      },
    ],
  },

  "rotational-dynamics": {
    keyIdea:
      "Newton's second law for rotation is τ_net = Iα — net torque causes angular acceleration, just as net force causes linear acceleration.",
    instructions:
      "Use the rotational inertia simulation to study the rotational form of Newton's second law. Apply known torques to objects with known moments of inertia and measure angular acceleration. Verify τ = Iα quantitatively. Explore scenarios with multiple torques (some clockwise, some counterclockwise) to find net torque and predict angular acceleration.",
    whatToTry: [
      "Apply a single torque and measure α. Verify α = τ/I.",
      "Apply two torques in opposite directions. Find the net torque and predict α.",
      "Keep torque constant and change I (by redistributing mass). How does α change?",
      "Keep I constant and vary torque. Verify the direct proportionality between τ and α.",
    ],
    guidedSteps: [
      {
        instruction:
          "Set I = 2 kg·m² and apply τ = 8 N·m. Predict α = τ/I = 8/2 = 4 rad/s².",
        observe:
          "The object accelerates at 4 rad/s². This is exactly like F = ma: net torque plays the role of force, moment of inertia plays the role of mass.",
      },
      {
        instruction:
          "Double the torque to 16 N·m (same I).",
        observe:
          "α doubles to 8 rad/s². Angular acceleration is directly proportional to net torque when I is constant.",
      },
      {
        instruction:
          "Reset torque to 8 N·m and double I to 4 kg·m².",
        observe:
          "α halves to 2 rad/s². Angular acceleration is inversely proportional to moment of inertia when torque is constant — harder to spin means slower angular acceleration.",
      },
      {
        instruction:
          "Apply τ₁ = 12 N·m clockwise and τ₂ = 5 N·m counterclockwise simultaneously. I = 2 kg·m².",
        observe:
          "Net torque = 12 - 5 = 7 N·m clockwise. α = 7/2 = 3.5 rad/s² clockwise. Just like net force, it is the NET torque that determines angular acceleration.",
      },
      {
        instruction:
          "Find the counterclockwise torque needed to produce rotational equilibrium (α = 0) when a 12 N·m clockwise torque is applied.",
        observe:
          "You need exactly 12 N·m counterclockwise. Net torque is zero, so α is zero. The object either remains stationary or rotates at constant angular velocity — the rotational analog of Newton's first law.",
      },
    ],
  },

  "angular-momentum": {
    keyIdea:
      "Angular momentum is conserved when no external torque acts — this is why a spinning ice skater speeds up when pulling their arms in: less I means more ω to keep L = Iω constant.",
    instructions:
      "Use the angular momentum simulation to explore L = Iω and its conservation. When no external torque acts, angular momentum is conserved. Change the moment of inertia (by moving mass closer to or farther from the axis) and watch angular velocity adjust to keep L constant. This is one of the most visually dramatic conservation laws in physics.",
    whatToTry: [
      "Spin an object and then change its moment of inertia by moving masses inward. Does ω increase?",
      "Start with arms out (large I) and pull in (small I). Calculate the ω ratio from the I ratio.",
      "Apply an external torque. Does angular momentum stay constant? (No — τ = dL/dt.)",
      "Verify L_initial = L_final numerically: I₁ω₁ = I₂ω₂.",
    ],
    guidedSteps: [
      {
        instruction:
          "Set a rotating platform with masses at r = 1m spinning at ω = 2 rad/s. If total I = 10 kg·m², then L = Iω = 20 kg·m²·/s.",
        observe:
          "Angular momentum L = 20 kg·m²/s. This is the rotational analog of linear momentum p = mv.",
      },
      {
        instruction:
          "While spinning (no external torque), pull the masses to r = 0.5m. New I = 2 × m × (0.5)² — it decreases.",
        observe:
          "Angular velocity ω increases! With masses at half the radius, I drops by a factor of 4 (since I ∝ r²), so ω must increase by a factor of 4 to conserve L. New ω = 8 rad/s.",
      },
      {
        instruction:
          "Verify: L = I₂ω₂. If I₂ = I₁/4 and ω₂ = 4ω₁, then L₂ = (I₁/4)(4ω₁) = I₁ω₁ = L₁.",
        observe:
          "Angular momentum is conserved: 20 kg·m²/s before and after. No external torque was applied, so L cannot change.",
      },
      {
        instruction:
          "Push the masses back out to r = 1m while still spinning.",
        observe:
          "ω decreases back to 2 rad/s. The process is reversible — angular momentum is always conserved as long as no external torque acts. This is the ice skater effect: arms out = slow spin, arms in = fast spin.",
      },
      {
        instruction:
          "Now apply an external torque while the system is spinning.",
        observe:
          "Angular momentum changes: τ = ΔL/Δt. A clockwise torque decreases L (or increases it if L was already clockwise). External torques can speed up, slow down, or change the direction of rotation.",
      },
      {
        instruction:
          "Remove the external torque again. Note the new (changed) L and verify it remains constant afterward.",
        observe:
          "Once the torque is removed, L stays at its new value. Angular momentum only changes while torque is applied. This parallels linear momentum, which only changes while a net force acts (impulse).",
      },
    ],
  },
};
